import React, { useCallback, useMemo, useState } from 'react';

import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { Tabs } from '@/components/shared/Tabs';
import {
	FilterReportKeysEnum,
	Songlist360Report,
} from '@/components/Songlist360Report';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { ChartSectionData, makeSection } from '@/helpers/reportsDataHelpers';
import { TagDistributionPoint } from '@/hooks/useReportsData';
import { DistributionCirclePackingChart } from '@/pages/Reports/components/DistributionCirclePackingChart/DistributionCirclePackingChart';
import { useTeamSongs } from '@/providers/TeamSongsProvider';

import styles from './Songs360Modal.module.scss';

interface Songs360ModalProps {
	open: boolean;
	onClose: () => void;
	songIds: string[];
}

export const Songs360Modal: React.FC<Songs360ModalProps> = ({
	open,
	onClose,
	songIds,
}) => {
	const [tabIndex, setTabIndex] = useState(0);
	const { findTeamSongsById } = useTeamSongs();

	const distributions = useMemo<{
		theme: ChartSectionData<TagDistributionPoint>;
		theology: ChartSectionData<TagDistributionPoint>;
		gospelStory: ChartSectionData<TagDistributionPoint>;
		emotion: ChartSectionData<TagDistributionPoint>;
		sharedLabels: {
			theme: string[];
			theology: string[];
			gospelStory: string[];
			emotion: string[];
		};
	}>(() => {
		const songs = findTeamSongsById(songIds).filter(Boolean) as NonNullable<
			ReturnType<typeof findTeamSongsById>[0]
		>[];

		const themeAcc: Record<string, number> = {};
		const theologyAcc: Record<string, number> = {};
		const gospelAcc: Record<string, number> = {};
		const emotionAcc: Record<string, number> = {};

		const themePerSong: Record<string, Set<string>> = {};
		const theologyPerSong: Record<string, Set<string>> = {};
		const gospelPerSong: Record<string, Set<string>> = {};
		const emotionPerSong: Record<string, Set<string>> = {};

		for (const song of songs) {
			for (const kw of song.analysisOutput?.thematicKeywords ?? []) {
				if (kw.theme && kw.strength != null) {
					themeAcc[kw.theme] =
						(themeAcc[kw.theme] ?? 0) + kw.strength;
					(themePerSong[kw.theme] ??= new Set()).add(song.id);
				}
			}
			for (const kw of song.analysisOutput?.theologicalKeywords ?? []) {
				if (kw.theology && kw.strength != null) {
					theologyAcc[kw.theology] =
						(theologyAcc[kw.theology] ?? 0) + kw.strength;
					(theologyPerSong[kw.theology] ??= new Set()).add(song.id);
				}
			}
			for (const tag of song.gospelTags ?? []) {
				gospelAcc[tag] = (gospelAcc[tag] ?? 0) + 1;
				(gospelPerSong[tag] ??= new Set()).add(song.id);
			}
			for (const e of song.analysisOutput?.emotions ?? []) {
				if (e.emotion && e.strength != null) {
					emotionAcc[e.emotion] =
						(emotionAcc[e.emotion] ?? 0) + e.strength;
					(emotionPerSong[e.emotion] ??= new Set()).add(song.id);
				}
			}
		}

		const toPoints = (
			acc: Record<string, number>,
		): TagDistributionPoint[] =>
			Object.entries(acc)
				.map(([label, count]) => ({ label, count }))
				.sort((a, b) => b.count - a.count);

		const sharedFrom = (perSong: Record<string, Set<string>>) =>
			Object.entries(perSong)
				.filter(([, s]) => s.size > 1)
				.map(([label]) => label);

		const theme = toPoints(themeAcc);
		const theology = toPoints(theologyAcc);
		const gospelStory = toPoints(gospelAcc);
		const emotion = toPoints(emotionAcc);

		return {
			theme: makeSection(theme, theme),
			theology: makeSection(theology, theology),
			gospelStory: makeSection(gospelStory, gospelStory),
			emotion: makeSection(emotion, emotion),
			sharedLabels: {
				theme: sharedFrom(themePerSong),
				theology: sharedFrom(theologyPerSong),
				gospelStory: sharedFrom(gospelPerSong),
				emotion: sharedFrom(emotionPerSong),
			},
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [songIds.join(','), findTeamSongsById]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	return (
		<AsaphDialog
			open={open}
			onClose={handleClose}
			paperClassName={styles['panel']}
		>
			<div className={styles['toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: (
							<AsaphDialogHeaderButton
								type="text"
								label="Done"
								handlePress={handleClose}
							/>
						),
					}}
					title={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label: 'Songs 360',
					}}
					trailingButton={{
						needsFadeIn: false,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: null,
					}}
				/>
			</div>
			<section className={styles['content']}>
				<div className={styles['tab-bar']}>
					<Tabs
						items={[{ title: 'Reports' }, { title: 'Analysis' }]}
						selectedIndex={tabIndex}
						onChange={setTabIndex}
					/>
				</div>
				<div className={styles['content-container']}>
					{tabIndex === 0 && (
						<div>
							<DistributionCirclePackingChart
								title="Themes"
								section={distributions.theme}
								defaultView="bubble"
								sharedLabels={distributions.sharedLabels.theme}
							/>
							<DistributionCirclePackingChart
								title="Theology"
								section={distributions.theology}
								defaultView="word-cloud"
								sharedLabels={distributions.sharedLabels.theology}
							/>
							<DistributionCirclePackingChart
								title="Gospel Story"
								section={distributions.gospelStory}
								defaultView="rank"
								semanticColors
								sharedLabels={distributions.sharedLabels.gospelStory}
							/>
							<DistributionCirclePackingChart
								title="Emotions"
								section={distributions.emotion}
								semanticColors
								sharedLabels={distributions.sharedLabels.emotion}
							/>
							<Songlist360Report
								songIds={songIds}
								filterReportKeys={[
									FilterReportKeysEnum.ENERGY,
									FilterReportKeysEnum.SONG_TYPE,
								]}
							/>
						</div>
					)}
					{tabIndex === 1 && (
						<div className={styles['analysis-placeholder']}>
							<p
								className={
									styles['analysis-placeholder__title']
								}
							>
								Analysis coming soon
							</p>
							<p className={styles['analysis-placeholder__body']}>
								AI-powered song analysis will appear here.
							</p>
						</div>
					)}
				</div>
			</section>
		</AsaphDialog>
	);
};
